// Lane B2's fallbacks for the Hiring Request screens, each to be replaced once its owner speaks or ships.
//
// QUESTION (Eduardo, through Tejesh; lane B2 report S08): the server still requires five V1 request fields that are not
// among his required request fields (G §5) — business unit, brands supported, reports-to title, and for a replacement
// the last day and "level and title unchanged". He has no word for them, so they cannot come from a labels module; they
// keep their V1 words here, in one fieldset of their own, until he names them or the requirement is dropped.

import type { RequestOpenOutput } from "@/api/tools";

export const V1_FIELD_WORDS = {
  businessUnit: "Business unit",
  brands: "Brands supported",
  reportsToTitle: "Reports-to title",
  lastDay: "Last day",
  levelAndTitleUnchanged: "Level and title unchanged",
} as const;

/** The brand slugs V1 requests carry, with the brand names (data, not words). */
export const BRAND_CHOICES: readonly { value: string; label: string }[] = [
  { value: "auntie_annes", label: "Auntie Anne's" },
  { value: "carvel", label: "Carvel" },
  { value: "cinnabon", label: "Cinnabon" },
  { value: "jamba", label: "Jamba" },
  { value: "mcalisters", label: "McAlister's Deli" },
  { value: "moes", label: "Moe's Southwest Grill" },
  { value: "schlotzskys", label: "Schlotzsky's" },
  { value: "corporate", label: "Corporate" },
];

/** The request fields the screen edits, as the form holds them (strings for inputs; null for an unset choice). */
export interface RequestDraft {
  title: string;
  hiring_manager_id: string;
  requester_id: string;
  level: "" | "manager_and_below" | "director_srdirector" | "vp_and_above";
  reason: "" | "new_headcount" | "backfill";
  vacating_person_ref: string;
  location: string;
  openings_requested: string;
  is_people_manager: boolean | null;
  direct_reports: string;
  posting_scope: "" | "internal_only" | "external" | "confidential";
  notes: string;
  department: string;
  job_family: string;
  area_of_interest: string;
  job_code: string;
  org_alignment: string;
  budget_status: "" | "funded" | "pending" | "unfunded";
  cost_center: string;
  justification: string;
  job_description: string;
  // Timing and budget (B13 F-143, DC-234): urgency has no preselection on a new request ("")
  target_start: string;
  urgency: "" | "normal" | "urgent";
  target_comp_min: string;
  target_comp_max: string;
  currency: string;
  position_no: string;
  // V1 fields with no word of his (above)
  business_unit: string;
  brand: string;
  reports_to_title: string;
  vacating_last_day: string;
  level_and_title_unchanged: boolean | null;
}

export const EMPTY_DRAFT: RequestDraft = {
  title: "", hiring_manager_id: "", requester_id: "", level: "", reason: "new_headcount", vacating_person_ref: "", location: "",
  openings_requested: "1", is_people_manager: null, direct_reports: "", posting_scope: "", notes: "", department: "",
  job_family: "", area_of_interest: "", job_code: "", org_alignment: "", budget_status: "pending", cost_center: "",
  justification: "", job_description: "", target_start: "", urgency: "", target_comp_min: "", target_comp_max: "", currency: "USD", position_no: "",
  business_unit: "", brand: "", reports_to_title: "", vacating_last_day: "",
  level_and_title_unchanged: null,
};

export function draftOf(r: RequestOpenOutput): RequestDraft {
  return {
    title: r.title ?? "",
    hiring_manager_id: r.intended_hm_id ?? "",
    requester_id: r.requester_id ?? "",
    level: (r.level || "") as RequestDraft["level"],
    reason: (r.reason || "new_headcount") as RequestDraft["reason"],
    vacating_person_ref: r.vacating_person_ref ?? "",
    location: (r.locations ?? []).join("; "),
    openings_requested: String(r.openings_requested ?? 1),
    is_people_manager: r.is_people_manager ?? null,
    direct_reports: r.direct_reports === null || r.direct_reports === undefined ? "" : String(r.direct_reports),
    posting_scope: (r.posting_scope || "") as RequestDraft["posting_scope"],
    notes: r.notes ?? "",
    department: r.department ?? "",
    job_family: r.job_family ?? "",
    area_of_interest: r.area_of_interest ?? "",
    job_code: r.job_code ?? "",
    org_alignment: r.org_alignment ?? "",
    budget_status: (r.budget_status || "pending") as RequestDraft["budget_status"],
    cost_center: r.cost_center ?? "",
    justification: r.justification ?? "",
    job_description: r.job_description ?? "",
    target_start: r.target_start ?? "",
    urgency: (r.urgency === "normal" || r.urgency === "urgent" ? r.urgency : "") as RequestDraft["urgency"],
    target_comp_min: r.target_comp_min ?? "",
    target_comp_max: r.target_comp_max ?? "",
    currency: r.currency || "USD",
    position_no: r.position_no ?? "",
    business_unit: r.business_unit ?? "",
    brand: r.brands?.[0] ?? "",
    reports_to_title: r.reports_to_title ?? "",
    vacating_last_day: r.vacating_last_day ?? "",
    level_and_title_unchanged: r.level_and_title_unchanged ?? null,
  };
}

const text = (value: string): string | null => (value.trim() ? value.trim() : null);
const count = (value: string): number | null => (value.trim() && Number.isFinite(Number(value)) ? Number(value) : null);
const money = (value: string): string | null => (value.trim() ? value.replace(/,/g, "").trim() : null);

/** The request columns of a draft, as the tools take them (no control inputs). */
export function fieldsOf(d: RequestDraft) {
  return {
    title: text(d.title),
    level: d.level || null,
    department: text(d.department),
    business_unit: text(d.business_unit),
    brands: d.brand ? [d.brand] : [],
    reports_to_title: text(d.reports_to_title),
    locations: d.location.split(";").map((part) => part.trim()).filter(Boolean),
    reason: d.reason || null,
    budget_status: d.budget_status || null,
    vacating_person_ref: d.reason === "backfill" ? text(d.vacating_person_ref) : null,
    vacating_last_day: d.reason === "backfill" && d.vacating_last_day ? d.vacating_last_day : null,
    level_and_title_unchanged: d.reason === "backfill" ? d.level_and_title_unchanged : null,
    openings_requested: count(d.openings_requested),
    justification: text(d.justification),
    posting_scope: d.posting_scope || null,
    is_people_manager: d.is_people_manager,
    direct_reports: d.is_people_manager ? count(d.direct_reports) : null,
    notes: text(d.notes),
    job_family: text(d.job_family),
    area_of_interest: text(d.area_of_interest),
    job_code: text(d.job_code),
    org_alignment: text(d.org_alignment),
    cost_center: text(d.cost_center),
    target_start: d.target_start || null,
    urgency: d.urgency || null,
    position_no: text(d.position_no),
    target_comp_min: money(d.target_comp_min),
    target_comp_max: money(d.target_comp_max),
    currency: d.currency || null,
  };
}

/** The columns the Hiring Manager's certification may correct (CorrectionFields): the target compensation range and its
 * currency are the HR Business Partner's working note, read-only there. */
export const NOT_CORRECTABLE = ["target_comp_min", "target_comp_max", "currency"] as const;

export type RequestColumns = ReturnType<typeof fieldsOf>;

/** The columns whose value differs between two drafts (the corrections a certification sends). */
export function changedColumns(before: RequestDraft, after: RequestDraft): Partial<RequestColumns> {
  const a = fieldsOf(before);
  const b = fieldsOf(after);
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(b) as (keyof RequestColumns)[]) {
    if (JSON.stringify(a[key]) !== JSON.stringify(b[key])) out[key] = b[key];
  }
  return out as Partial<RequestColumns>;
}
